import { useEffect, useState } from "react";
import { runtimeConfig } from "../config/runtime";
import { HydratedEvent } from "../services/eventsService";
import { isHomeCode } from "../utils/homeScreen";
import { getCurrentScreenCode } from "../utils/screenStorage";

export interface LabbaikItem {
  kind: "event" | "announcement" | "appeal";
  title?: string;
  text?: string;
  detail?: string;
  imageUrl?: string;
  linkUrl?: string;
  linkLabel?: string;
  note?: string;
  progressPercent?: number;
  startDate?: string;
  startTime?: string;
  endTime?: string;
  repeatDays?: string[];
  timeText?: string;
  location?: string;
}

const REFRESH_MS = 15 * 60 * 1000;
const CHECK_MS = 60 * 1000;
const SLIDE_SECONDS = 15;
const EMPTY: LabbaikItem[] = [];

// Module-level and shared by every caller (DisplayController, the playlist display
// region, both slideshows): one fetch per masjid every 15 minutes, and a slideshow
// that remounts every cycle starts with the items already known.
const cache = new Map<number, { items: LabbaikItem[]; json: string; fetchedAt: number }>();
const inflight = new Map<number, Promise<void>>();

// The backend rewrites jsonfiles/labbaik/{masjidId}.json (the masjid's own screens) and
// {masjidId}.app.json (its public version, without "prayer board only" posts) every 15 minutes.
// Household tablets get the public one, as the website does. A 404 means the masjid has
// nothing to show; any other failure keeps the last items, and the service worker serves
// the last copy when offline.
async function fetchItems(masjidId: number) {
  try {
    const feed = isHomeCode(getCurrentScreenCode()) ? "app.json" : "json";
    const response = await fetch(
      `${runtimeConfig.storageUrl}/jsonfiles/labbaik/${masjidId}.${feed}`,
      { signal: AbortSignal.timeout(10000) },
    );
    if (response.status !== 404 && !response.ok) return;
    const data = response.ok ? await response.json() : null;
    const next: LabbaikItem[] = Array.isArray(data?.items) ? data.items : [];

    // Warm the image cache so posters still show if the network drops later.
    next.forEach((item) => {
      if (item.imageUrl) fetch(item.imageUrl, { mode: "cors" }).catch(() => {});
    });

    const json = JSON.stringify(next);
    const previous = cache.get(masjidId);
    const items = previous?.json === json ? previous.items : next;
    cache.set(masjidId, { items, json, fetchedAt: Date.now() });
  } catch {
    // keep the last items; the next check retries
  }
}

function refresh(masjidId: number): Promise<void> {
  const cached = cache.get(masjidId);
  if (cached && Date.now() - cached.fetchedAt < REFRESH_MS) return Promise.resolve();
  let pending = inflight.get(masjidId);
  if (!pending) {
    pending = fetchItems(masjidId).finally(() => inflight.delete(masjidId));
    inflight.set(masjidId, pending);
  }
  return pending;
}

export default function useLabbaikItems(
  masjidId: number | undefined,
  enabled: boolean | undefined,
): LabbaikItem[] {
  const [items, setItems] = useState<LabbaikItem[]>(
    () => (enabled && masjidId && cache.get(masjidId)?.items) || EMPTY,
  );

  useEffect(() => {
    if (!enabled || !masjidId) {
      setItems(EMPTY);
      return;
    }

    let cancelled = false;
    const sync = () =>
      refresh(masjidId).then(() => {
        if (!cancelled) setItems(cache.get(masjidId)?.items ?? EMPTY);
      });

    sync();
    const interval = window.setInterval(sync, CHECK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [masjidId, enabled]);

  return items;
}

export function labbaikSlideshowItems(items: LabbaikItem[]) {
  return items.map((item, index) => ({
    id: `labbaik-${index}`,
    assetType: "labbaik",
    labbaik: item,
    duration: SLIDE_SECONDS,
    displayOrder: 996,
    isSpecialItem: true,
  }));
}

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function dateKey(day: Date) {
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
}

function atTime(day: Date, time: string) {
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, minutes);
}

// Labbaik events in the events panel's shape for the `days` days from `from`. Weekly
// events become one entry per matching day. Salah-time events ("After Maghrib") have no
// clock time, so they sit at 23:59 to sort last and stay listed until the day ends.
export function labbaikEvents(items: LabbaikItem[], from: Date, days = 7): HydratedEvent[] {
  const events: HydratedEvent[] = [];
  items.forEach((item, index) => {
    if (item.kind !== "event" || !item.title) return;
    for (let i = 0; i < days; i++) {
      const day = new Date(from.getFullYear(), from.getMonth(), from.getDate() + i);
      const onDay = item.repeatDays
        ? item.repeatDays.includes(WEEK_DAYS[day.getDay()])
        : item.startDate === dateKey(day);
      if (!onDay) continue;

      const event: HydratedEvent = {
        uid: `labbaik-${index}-${dateKey(day)}`,
        summary: item.title,
        description: item.text,
        location: item.location,
        start: atTime(day, "00:00"),
      };
      if (item.startTime) {
        event.start = atTime(day, item.startTime);
        if (item.endTime) event.end = atTime(day, item.endTime);
      } else if (item.timeText) {
        event.start = atTime(day, "23:59");
        event.timeLabel = item.timeText;
      } else {
        event.allDay = true;
      }
      events.push(event);
    }
  });
  return events;
}
